import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const [trackingId, setTrackingId] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    if (trackingId.trim()) {
      navigate(`/track?q=${encodeURIComponent(trackingId.trim())}`);
    } else {
      navigate('/track');
    }
  };

  const quickIds = ['SWT-2024-001847', 'SWT-2024-002391', 'SWT-2024-003104'];

  const stats = [
    { value: '2.4M+', key: 'hero.stat1' },
    { value: '180+', key: 'hero.stat2' },
    { value: '99.7%', key: 'hero.stat3' },
    { value: '24/7', key: 'hero.stat4' },
  ];

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=massive%20white%20cargo%20freight%20airplane%20soaring%20above%20dramatic%20golden%20cumulus%20clouds%2C%20wide%20angle%20aerial%20photography%20from%20below%2C%20warm%20amber%20orange%20sky%20at%20sunset%2C%20professional%20aviation%20cinematic%20photography%2C%20ultra%20wide%20angle%20lens%2C%20high%20resolution%208k%2C%20breathtaking%20perspective%2C%20clean%20white%20aircraft%20fuselage%20against%20glowing%20sky&width=1920&height=1080&seq=hero1&orientation=landscape"
          alt="Cargo aircraft in flight"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(7,40,40,0.4) 0%, rgba(0,0,0,0.1) 100%)' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-32 pb-20 flex flex-col items-center text-center">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider mb-8 border"
          style={{ background: 'rgba(13,148,136,0.2)', borderColor: 'rgba(13,148,136,0.4)', color: '#5eead4' }}
        >
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--primary)' }} />
          {t('hero.badge')}
        </div>

        {/* Headline */}
        <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl text-white leading-tight max-w-4xl mb-6">
          {t('hero.headline1')}{' '}
          <span style={{ color: 'var(--primary)' }}>{t('hero.headline2')}</span>
        </h1>

        <p className="text-white/75 text-lg md:text-xl max-w-2xl mb-12 leading-relaxed">
          {t('hero.subtext')}
        </p>

        {/* Tracking form */}
        <form onSubmit={handleTrack} className="w-full max-w-2xl">
          <div className="flex flex-col sm:flex-row gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-2">
            <div className="flex-1 flex items-center gap-3 bg-white rounded-xl px-4 py-3">
              <i className="ri-search-line text-gray-400 text-lg flex-shrink-0" />
              <input
                type="text"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                placeholder={t('hero.placeholder')}
                className="flex-1 bg-transparent text-gray-800 text-sm outline-none placeholder-gray-400 min-w-0"
              />
            </div>
            <button
              type="submit"
              className="px-7 py-3 rounded-xl text-sm font-semibold text-white whitespace-nowrap transition-opacity hover:opacity-90 cursor-pointer"
              style={{ background: 'var(--primary)' }}
            >
              {t('hero.trackBtn')}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-4 justify-center flex-wrap">
            <span className="text-white/40 text-xs">{t('hero.tryLabel')}</span>
            {quickIds.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setTrackingId(id)}
                className="text-xs text-white/60 hover:text-white border border-white/20 hover:border-white/40 rounded-full px-3 py-1 transition-colors cursor-pointer"
              >
                {id}
              </button>
            ))}
          </div>
        </form>

        {/* Trust stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 w-full max-w-3xl">
          {stats.map(({ value, key }) => (
            <div key={key} className="text-center">
              <div className="font-heading font-bold text-3xl text-white mb-1">{value}</div>
              <div className="text-white/50 text-xs tracking-wide">{t(key)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs">{t('hero.scroll')}</span>
        <i className="ri-arrow-down-line text-white/40" />
      </div>
    </section>
  );
}
